import { __rest } from '../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Badge } from '../Components/ui/badge.js';
import { Carousel, CarouselContent, CarouselItem } from '../Components/ui/carousel.js';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../Components/ui/hover-card.js';
import { cn } from '../lib/utils.js';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { createContext, useState, useEffect, useCallback, useContext } from 'react';

const InlineCitation = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (jsx("span", Object.assign({ className: cn("tw:group tw:inline tw:items-center tw:gap-1", className) }, props)));
};
const InlineCitationText = (_a) => {
    var { className, url, children } = _a, props = __rest(_a, ["className", "url", "children"]);
    if (url) {
        return (jsx("a", Object.assign({ href: url, target: "_blank", rel: "noopener noreferrer", className: cn("tw:transition-colors tw:group-hover:bg-accent tw:cursor-pointer tw:underline", className) }, props, { children: children })));
    }
    return (jsx("span", Object.assign({ className: cn("tw:transition-colors tw:group-hover:bg-accent", className) }, props, { children: children })));
};
const InlineCitationCard = (props) => (jsx(HoverCard, Object.assign({ closeDelay: 0, openDelay: 0 }, props)));
const InlineCitationCardTrigger = (_a) => {
    var { sources, className } = _a, props = __rest(_a, ["sources", "className"]);
    return (jsx(HoverCardTrigger, Object.assign({ asChild: true }, { children: jsx(Badge, Object.assign({ className: cn("tw:ml-1 tw:rounded-full", className), variant: "secondary" }, props, { children: sources[0] ? (jsxs(Fragment, { children: [new URL(sources[0]).hostname, " ", sources.length > 1 && `+${sources.length - 1}`] })) : ("unknown") })) })));
};
const InlineCitationCardBody = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (jsx(HoverCardContent, Object.assign({ className: cn("tw:relative tw:w-80 tw:p-0", className) }, props)));
};
const CarouselApiContext = createContext(undefined);
const useCarouselApi = () => {
    const context = useContext(CarouselApiContext);
    return context;
};
const InlineCitationCarousel = (_a) => {
    var { className, children } = _a, props = __rest(_a, ["className", "children"]);
    const [api, setApi] = useState();
    return (jsx(CarouselApiContext.Provider, Object.assign({ value: api }, { children: jsx(Carousel, Object.assign({ className: cn("tw:w-full", className), setApi: setApi }, props, { children: children })) })));
};
const InlineCitationCarouselContent = (props) => jsx(CarouselContent, Object.assign({}, props));
const InlineCitationCarouselItem = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (jsx(CarouselItem, Object.assign({ className: cn("tw:w-full tw:space-y-2 tw:p-4 tw:pl-8", className) }, props)));
};
const InlineCitationCarouselHeader = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (jsx("div", Object.assign({ className: cn("tw:flex tw:items-center tw:justify-between tw:gap-2 tw:rounded-t-md tw:bg-secondary tw:p-2", className) }, props)));
};
const InlineCitationCarouselIndex = (_a) => {
    var { children, className } = _a, props = __rest(_a, ["children", "className"]);
    const api = useCarouselApi();
    const [current, setCurrent] = useState(0);
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!api) {
            return;
        }
        setCount(api.scrollSnapList().length);
        setCurrent(api.selectedScrollSnap() + 1);
        api.on("select", () => {
            setCurrent(api.selectedScrollSnap() + 1);
        });
    }, [api]);
    return (jsx("div", Object.assign({ className: cn("tw:flex tw:flex-1 tw:items-center tw:justify-end tw:px-3 tw:py-1 tw:text-muted-foreground tw:text-xs", className) }, props, { children: children !== null && children !== void 0 ? children : `${current}/${count}` })));
};
const InlineCitationCarouselPrev = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const api = useCarouselApi();
    const handleClick = useCallback(() => {
        if (api) {
            api.scrollPrev();
        }
    }, [api]);
    return (jsx("button", Object.assign({ "aria-label": "Previous", className: cn("tw:shrink-0", className), onClick: handleClick, type: "button" }, props, { children: jsx(ArrowLeftIcon, { className: "tw:size-4 tw:text-muted-foreground" }) })));
};
const InlineCitationCarouselNext = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const api = useCarouselApi();
    const handleClick = useCallback(() => {
        if (api) {
            api.scrollNext();
        }
    }, [api]);
    return (jsx("button", Object.assign({ "aria-label": "Next", className: cn("tw:shrink-0", className), onClick: handleClick, type: "button" }, props, { children: jsx(ArrowRightIcon, { className: "tw:size-4 tw:text-muted-foreground" }) })));
};
const InlineCitationSource = (_a) => {
    var { title, url, description, className, children } = _a, props = __rest(_a, ["title", "url", "description", "className", "children"]);
    return (jsxs("div", Object.assign({ className: cn("tw:space-y-1", className) }, props, { children: [title && (jsx("h4", Object.assign({ className: "tw:truncate tw:font-medium tw:text-sm tw:leading-tight" }, { children: url ? (jsx("a", Object.assign({ href: url, target: "_blank", rel: "noopener noreferrer", className: "tw:cursor-pointer tw:hover:underline" }, { children: title }))) : (title) }))), url && (jsx("a", Object.assign({ href: url, target: "_blank", rel: "noopener noreferrer", className: "tw:truncate tw:break-all tw:text-muted-foreground tw:text-xs tw:cursor-pointer tw:hover:underline tw:block" }, { children: url }))), description && (jsx("p", Object.assign({ className: "tw:line-clamp-3 tw:text-muted-foreground tw:text-sm tw:leading-relaxed" }, { children: description }))), children] })));
};

export { InlineCitation, InlineCitationCard, InlineCitationCardBody, InlineCitationCardTrigger, InlineCitationCarousel, InlineCitationCarouselContent, InlineCitationCarouselHeader, InlineCitationCarouselIndex, InlineCitationCarouselItem, InlineCitationCarouselNext, InlineCitationCarouselPrev, InlineCitationSource, InlineCitationText };
//# sourceMappingURL=inline-citation.js.map
